import * as React from "react";

import type { ArtifactDetail } from "@/api/artifacts";
import { ApiError } from "@/api/client";
import { useArtifactPayloadText } from "@/api/payloads";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { isStructureBearingType, pickViewablePayload } from "@/components/viz/molecule-structure";
import { Skeleton } from "@/components/ui/skeleton";
import { truncateMiddle } from "@/lib/hash-display";

/**
 * Lazy boundary for the 3D viewer. The `import()` is the ONLY route into
 * `MoleculeViewer`, and therefore into `3dmol` — so 3Dmol.js lands in its own
 * chunk that is requested when a user opens a structure-bearing artifact, and
 * never on the run list, the artifact list, or any other route.
 *
 * Same pattern as the reactflow DAG canvas (`SubmitFlow.tsx`), which is this
 * codebase's established precedent for a heavy widget.
 */
const MoleculeViewer = React.lazy(() => import("@/components/viz/MoleculeViewer"));

export interface MoleculeViewerPanelProps {
  artifact: ArtifactDetail;
  /**
   * Heading shown above the structure. Defaults to "Structure".
   *
   * The submission preview passes the port name, because there the
   * question a user is asking is "what did I put in *ligand*", not "what
   * is this artifact" — the artifact identity is already on screen in the
   * select they just used.
   */
  heading?: string;
  /**
   * Grow into the available height rather than the viewer's fixed box
   * (flowstack#956 — the expanded workspace). Threaded to `MoleculeViewer`;
   * the panel's own chrome becomes a flex column so the canvas can take the
   * surplus.
   */
  fill?: boolean;
}

/**
 * Renders an artifact's structure, when the artifact's TYPE declares one.
 *
 * Type-addressed end to end (VISUALIZATION_PLAN §5 / C2): everything this
 * panel decides comes from `artifact.typed_object` and the registered payload
 * names. It never reads `producer_tile`, so any flow producing a `Molecule`
 * gets this viewer with no wiring — which is the whole reason the rule exists.
 *
 * Returns `null` for a type that carries no structure. That is not a hidden
 * state: a `ForceFieldBundle` has no molecule to draw, and an empty "no
 * structure" box on every JSON-only artifact would be noise. A type that
 * *should* carry a structure and does not says so explicitly below — that
 * absence is a real finding about the artifact, and contract C3 says it must
 * be visible rather than implied.
 */
export function MoleculeViewerPanel({
  artifact,
  heading = "Structure",
  fill = false,
}: MoleculeViewerPanelProps): JSX.Element | null {
  const viewable = pickViewablePayload(artifact.typed_object, artifact.payloads ?? []);
  const { data, isLoading, error } = useArtifactPayloadText(viewable?.payload.download_url ?? null);
  // `useId`, not a constant: since #950 wired this panel into the
  // submission form, a page can mount SEVERAL of these at once (one per
  // molecule-typed input port). A literal id would repeat, which is
  // invalid HTML and — worse, because it fails silently — points every
  // `aria-labelledby` at whichever heading happened to render first.
  const headingId = React.useId();

  if (viewable === null) {
    if (!isStructureBearingType(artifact.typed_object)) return null;
    return (
      <Panel headingId={headingId} heading={heading}>
        <p className="text-sm text-muted-foreground">
          This {artifact.typed_object} has no structure file in the catalog, so there is nothing to
          render. The typed object&rsquo;s metadata is above.
        </p>
      </Panel>
    );
  }

  if (isLoading) {
    return (
      <Panel headingId={headingId} heading={heading}>
        <Skeleton className="h-[420px] w-full" />
      </Panel>
    );
  }

  if (error) {
    // A 413 is a different fact from a failure: the structure exists and is
    // fine, it is simply past what a browser tab should parse. Say which,
    // because the two have different remedies — download vs. report a bug.
    const tooLarge = error instanceof ApiError && error.errorCode === "payload_too_large";
    return (
      <Panel headingId={headingId} heading={heading}>
        <p className="text-sm text-muted-foreground">
          {tooLarge
            ? `${viewable.payload.payload_name} is too large to render in the browser. Download it to view it locally.`
            : `Could not load ${viewable.payload.payload_name}.`}
        </p>
      </Panel>
    );
  }

  if (data === undefined || data.trim() === "") {
    return (
      <Panel headingId={headingId} heading={heading}>
        <p className="text-sm text-muted-foreground">
          {viewable.payload.payload_name} is empty, so there is nothing to render.
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      headingId={headingId}
      heading={heading}
      payloadName={viewable.payload.payload_name}
      fill={fill}
    >
      {/* ErrorBoundary per ErrorBoundary.tsx's own docstring: any sub-tree
          mounting a third-party widget that could throw on a malformed
          payload. A structure file that parses on the server but trips the
          browser parser must cost this panel, not the page. */}
      <ErrorBoundary
        fallback={() => (
          <p className="text-sm text-muted-foreground">
            {viewable.payload.payload_name} could not be rendered as a 3D structure.
          </p>
        )}
      >
        <React.Suspense
          fallback={
            <Skeleton className={fill ? "h-full min-h-0 w-full flex-1" : "h-[420px] w-full"} />
          }
        >
          <MoleculeViewer
            structureText={data}
            format={viewable.format}
            label={`3D structure of ${artifact.typed_object} ${artifact.content_hash}`}
            fill={fill}
          />
        </React.Suspense>
      </ErrorBoundary>
    </Panel>
  );
}

function Panel({
  headingId,
  heading,
  payloadName,
  fill = false,
  children,
}: {
  headingId: string;
  heading: string;
  payloadName?: string;
  fill?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section
      className={fill ? "flex min-h-0 flex-1 flex-col gap-2" : "space-y-2"}
      aria-labelledby={headingId}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 id={headingId} className="min-w-0 truncate text-sm font-medium text-foreground">
          {heading}
        </h2>
        {payloadName !== undefined && (
          // ELIDED, and elided in the MIDDLE (flowstack#1223 F). A payload is
          // routinely named for its content hash, so this row read as
          // `Input to fragment_topology   8401ed6e9bbfbd757dfd5ce922a86716` —
          // a 64-character identifier cut dead at the panel edge with no
          // ellipsis, which is indistinguishable from a short id that simply
          // ends there. `shrink-0` so the elision is this span's decision at a
          // width it controls, rather than the flex row's at whatever width is
          // left over; the full value is on `title`, as it is everywhere else
          // an identifier is shortened.
          <span
            className="shrink-0 font-mono text-xs text-muted-foreground"
            title={payloadName}
            data-testid="viewer-payload-name"
          >
            {truncateMiddle(payloadName)}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}
